import { useEffect, useRef, useState } from "react";

/**
 * Tracks how far the element's center sits from the viewport's center as
 * the page scrolls, scaled by `speed`, for a simple scroll-parallax
 * translateY. Updates on scroll/resize only (no permanent rAF loop), and
 * does nothing when the visitor prefers reduced motion.
 */
export function useParallax<T extends HTMLElement = HTMLDivElement>(
  speed = 0.2,
) {
  const ref = useRef<T>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let ticking = false;

    const measure = () => {
      const el = ref.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const viewportCenter = window.innerHeight / 2;
        const elementCenter = rect.top + rect.height / 2;
        setOffset((viewportCenter - elementCenter) * speed);
      }
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(measure);
      }
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [speed]);

  return { ref, offset };
}
